import React, { useRef, useState, useCallback } from 'react';
import api from '../../api';

const PHOTO_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic'];
const VIDEO_EXTS = ['.mp4', '.mov', '.webm', '.m4v'];
const ACCEPT_ATTR = [...PHOTO_EXTS, ...VIDEO_EXTS].join(',');

function getFileMediaType(file) {
  const name = (file.name || '').toLowerCase();
  const ext = name.substring(name.lastIndexOf('.'));
  if (PHOTO_EXTS.includes(ext)) return 'photo';
  if (VIDEO_EXTS.includes(ext)) return 'video';
  return null;
}

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Multi-file uploader with drag-and-drop and per-file progress.
 *
 * Props:
 *   endpoint        – URL to POST each file to
 *   commonFields    – FormData fields shared by every upload (year, event_id, event_name)
 *   onSuccess       – function (file, response)
 *   onError         – function (file, error)
 *   disabled        – bool
 */
export default function MultiFileUploader({
  endpoint = '/api/gallery/upload',
  commonFields = {},
  onSuccess,
  onError,
  disabled = false,
}) {
  const [queue, setQueue] = useState([]); // { id, file, mediaType, status, progress, error }
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  const removeFromQueue = useCallback((id) => {
    setQueue((q) => q.filter((it) => it.id !== id));
  }, []);

  const uploadOne = useCallback(
    async (item) => {
      const { id, file, mediaType } = item;
      if (!mediaType) return;
      setQueue((q) => q.map((it) => (it.id === id ? { ...it, status: 'uploading', progress: 0, error: null } : it)));
      const form = new FormData();
      Object.entries(commonFields).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') form.append(k, v);
      });
      form.append('media_type', mediaType);
      form.append('item_name', file.name.replace(/\.[^.]+$/, ''));
      form.append('file', file);
      try {
        const response = await api.post(endpoint, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt) => {
            if (evt.total) {
              const pct = Math.round((evt.loaded * 100) / evt.total);
              setQueue((q) => q.map((it) => (it.id === id ? { ...it, progress: pct } : it)));
            }
          },
        });
        setQueue((q) => q.map((it) => (it.id === id ? { ...it, status: 'done', progress: 100 } : it)));
        onSuccess?.(file, response.data);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || 'Upload failed';
        setQueue((q) => q.map((it) => (it.id === id ? { ...it, status: 'error', error: msg } : it)));
        onError?.(file, err);
      }
    },
    [commonFields, endpoint, onError, onSuccess]
  );

  const uploadAll = useCallback(async () => {
    setQueue((q) => q.map((it) => (it.status === 'pending' ? { ...it, status: 'uploading', progress: 0 } : it)));
    const items = queue.filter((it) => it.status === 'pending' || it.status === 'uploading');
    for (const it of items) {
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(it);
    }
  }, [queue, uploadOne]);

  const clearCompleted = useCallback(() => {
    setQueue((q) => q.filter((it) => it.status !== 'done'));
  }, []);

const pendingCount = queue.filter((it) => it.status === 'pending').length;
  const uploadingCount = queue.filter((it) => it.status === 'uploading').length;
  const doneCount = queue.filter((it) => it.status === 'done').length;
  const errorCount = queue.filter((it) => it.status === 'error').length;

  const addFiles = useCallback((files) => {
    const newItems = Array.from(files).map((file) => {
      const mediaType = getFileMediaType(file);
      return {
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        file,
        mediaType,
        status: mediaType ? 'pending' : 'error',
        progress: 0,
        error: mediaType ? null : 'Unsupported file type',
      };
    });
    setQueue((q) => [...q, ...newItems]);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      if (disabled) return;
      const files = e.dataTransfer?.files;
      if (files?.length) addFiles(files);
    },
    [addFiles, disabled]
  );

  return (
    <div className="multi-file-uploader">
      <div
        className={`multi-file-dropzone${isDragging ? ' is-dragging' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        aria-label="Click or drag files to upload"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          onChange={(e) => {
            if (e.target.files?.length) addFiles(e.target.files);
            e.target.value = '';
          }}
          disabled={disabled}
          style={{ display: 'none' }}
          aria-hidden="true"
        />
        <div className="dropzone-icon" aria-hidden="true">📁</div>
        <div className="dropzone-text">
          <strong>Click to choose files</strong> or drag and drop
        </div>
        <div className="dropzone-hint">
          Photos: JPG, PNG, GIF, WEBP, HEIC (≤ 15 MB each) · Videos: MP4, MOV, WEBM, M4V (≤ 200 MB each)
        </div>
      </div>

      {queue.length > 0 && (
        <div className="multi-file-queue">
          <div className="multi-file-queue-header">
            <span>
              {queue.length} file{queue.length === 1 ? '' : 's'} queued
              {doneCount > 0 && ` · ${doneCount} done`}
              {errorCount > 0 && ` · ${errorCount} failed`}
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {doneCount > 0 && (
                <button type="button" className="btn btn-sm btn-secondary" onClick={clearCompleted}>
                  Clear done
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={uploadAll}
                disabled={disabled || pendingCount === 0 || uploadingCount > 0}
              >
                {uploadingCount > 0 ? `Uploading (${uploadingCount})…` : `Upload ${pendingCount}`}
              </button>
            </div>
          </div>

          <ul className="multi-file-list">
            {queue.map((it) => (
              <li key={it.id} className={`multi-file-item status-${it.status}`}>
                <span className="multi-file-icon" aria-hidden="true">
                  {it.mediaType === 'video' ? '🎬' : '🖼️'}
                </span>
                <div className="multi-file-meta">
                  <div className="multi-file-name" title={it.file.name}>{it.file.name}</div>
                  <div className="multi-file-sub">
                    {formatBytes(it.file.size)} · {it.mediaType || 'unsupported'}
                  </div>
                  {it.status === 'uploading' && (
                    <div className="multi-file-progress">
                      <div className="multi-file-progress-bar" style={{ width: `${it.progress}%` }} />
                    </div>
                  )}
                  {it.status === 'error' && it.error && (
                    <div className="multi-file-error">{it.error}</div>
                  )}
                </div>
                <button
                  type="button"
                  className="multi-file-remove"
                  onClick={() => removeFromQueue(it.id)}
                  aria-label={`Remove ${it.file.name}`}
                  disabled={it.status === 'uploading'}
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}